/**
 * Scene loader plugin for the **bundle** format — `.glz` (spec §4), and the
 * legacy `.tracex` it replaces.
 *
 * A bundle is a ZIP archive containing:
 * - `scene.gltf` (or a legacy `scene.glxf`) at the archive ROOT: the scene
 *   document with Trace extras
 * - `assets/`: the folder's files, named humanly (`assets/chair.glb`) by the
 *   writer from `files[k].name`, or by hash when the document carries none
 *
 * Extends GltfSceneLoaderPlugin to add ZIP extraction with asset blobs for
 * ResourceManager. Trace extras (active, behaviors, KHR_interactivity
 * restoration) are handled by the base plugin and apply to a standalone
 * document too.
 *
 * Unlike a standalone document, a bundle does NOT load GLBs inline.
 * Instead it stores asset blobs in the LoadedSceneResult for ResourceManager,
 * and sets `src` on each node so Model/Image/Video behaviors load assets.
 * This matches the .trace plugin pattern that TraceScene.loadFromPluginResult expects.
 *
 * `.tracex` is READ-ONLY from here (spec §5): the writer emits `.glz`, and the
 * extension is deleted with its rows once the migration has run (#816).
 *
 * Transform semantic: PRESERVE
 * - Asset's internal transform is preserved
 * - Scene transform composes with asset transform (glTF behavior)
 */

import JSZip from 'jszip';
import { GltfSceneLoaderPlugin, ParsedDocumentNode, ParsedDocumentStructure } from './GltfSceneLoaderPlugin';
import type { ISceneLoaderPluginExtensions, ISceneLoaderProgressEvent } from "@babylonjs/core/Loading/sceneLoader";
import type { Scene } from "@babylonjs/core/scene";
import type { NormalizedNode, NormalizedSceneDocument } from '../formats/gltf21/types';
import { SCENE_DOCUMENT_ARCHIVE_ENTRIES } from '../../scenedata/constants';
import { toZipBytes } from '../formats/bundle/zipSniff';
import type { LoadedSceneNode, LoadedSceneResult } from './LoadedSceneResult';
import { loadNodesWithResult as loadNodesWithResultUtil } from './sceneLoadingUtils';
import { Utility } from '../../utils/Utility';

// =============================================================================
// Types for Trace extras in the scene document
// =============================================================================

/**
 * Extended node type with bundle-specific properties. Trace extras
 * (`active`, `behaviors`, KHR_interactivity restoration) live on the base
 * `ParsedDocumentNode.extras` and are forwarded by the base
 * `extractNodeMetadata` — only the ZIP-asset path is bundle-only.
 */
export interface BundleParsedNode extends ParsedDocumentNode {
    /** Original asset path from the document (e.g. "assets/model.glb") — NOT the blob URL */
    originalAssetPath?: string;
}

/**
 * Extended structure with asset blobs extracted from the ZIP
 */
interface BundleParsedStructure extends ParsedDocumentStructure {
    /** Asset blobs extracted from the ZIP, keyed as "assets/filename" for ResourceManager */
    assetBlobs: Map<string, Blob>;
}

// =============================================================================
// Plugin Implementation
// =============================================================================

export class BundleSceneLoaderPlugin extends GltfSceneLoaderPlugin {
    override readonly name: string = "bundle";
    /**
     * `.glz` is the format; `.tracex` is the same container under its retired
     * name and stays readable until its rows are migrated (spec §5, #816).
     */
    override readonly extensions: ISceneLoaderPluginExtensions = {
        ".glz": { isBinary: true },
        ".tracex": { isBinary: true },
    };

    /**
     * Parse a bundle ZIP archive into the common scene structure.
     * Extracts assets as Blobs (for ResourceManager) and blob URLs (for asset resolution).
     */
    protected override async parseSceneData(
        data: unknown,
        rootUrl: string,
        fileName?: string
    ): Promise<BundleParsedStructure> {
        // Load ZIP archive. `toZipBytes` rather than a cast: an ArrayBuffer
        // that crossed a realm fails JSZip's `instanceof` check with nothing
        // wrong with the bytes (see the note on that helper).
        const zip = await JSZip.loadAsync(toZipBytes(data));

        // Read the scene document. Archives written before the glTF 2.1
        // cutover carry `scene.glxf`; both names read (spec §4, §5).
        const sceneFile = SCENE_DOCUMENT_ARCHIVE_ENTRIES
            .map(entry => zip.file(entry))
            .find(file => !!file);
        if (!sceneFile) {
            throw new Error(
                `Archive does not contain ${SCENE_DOCUMENT_ARCHIVE_ENTRIES.join(' or ')}`
            );
        }
        const document = this.parseDocument(await sceneFile.async('string'));

        // Extract assets — both blob URLs (for asset resolution) and Blobs (for ResourceManager)
        const { assetBlobUrls, assetBlobs } = await this.extractAssets(zip);

        // Parse the document nodes, normalize to one document root (§3), then
        // restore any document-level KHR_interactivity onto it. The base
        // implementation does this in its parseSceneData, but we've overridden
        // parseSceneData to unpack the ZIP, so call it explicitly here.
        const nodes = this.parseDocumentNodes(document, rootUrl, assetBlobUrls);
        const migration = this.migrateDocumentRoot(document, nodes, fileName);
        this.restoreKHRInteractivity(document, nodes, migration);

        return {
            nodes,
            assetBlobUrls,
            assetBlobs,
            rootUrl,
            document,
            ...(migration.root ? { documentRoot: migration.root } : {}),
            ...(migration.scripts ? { scripts: migration.scripts } : {}),
        };
    }

    /**
     * Extract assets from ZIP to both blob URLs and Blob objects
     */
    private async extractAssets(zip: JSZip): Promise<{
        assetBlobUrls: Map<string, string>;
        assetBlobs: Map<string, Blob>;
    }> {
        const assetBlobUrls = new Map<string, string>();
        const assetBlobs = new Map<string, Blob>();
        const assetsFolder = zip.folder('assets');

        if (assetsFolder) {
            const assetFiles = assetsFolder.filter((_relativePath, file) => !file.dir);
            for (const file of assetFiles) {
                const arrayBuffer = await file.async('arraybuffer');

                // Extract just the filename
                const assetFilename = file.name.replace(/^assets\//, '');
                const mimeType = Utility.mimeTypeFromFilename(assetFilename);
                const blob = new Blob([arrayBuffer], mimeType ? { type: mimeType } : undefined);
                const blobUrl = URL.createObjectURL(blob);
                assetBlobUrls.set(assetFilename, blobUrl);

                // Store as "assets/filename" for ResourceManager (matches ./assets/filename src paths)
                assetBlobs.set(`assets/${assetFilename}`, blob);

                // Track for cleanup
                this.trackBlobUrl(blobUrl);
            }
        }

        return { assetBlobUrls, assetBlobs };
    }

    /**
     * Override to track the original (pre-blob-URL) asset path for use as
     * `src`. Trace extras (`active`, `behaviors`) are already preserved on
     * `parsedNode.extras` by the base plugin and forwarded by the base
     * `extractNodeMetadata` — no TraceX-specific extras code is needed.
     */
    protected override convertNode(
        documentNode: NormalizedNode,
        nodeIndex: number,
        document: NormalizedSceneDocument,
        nodeNames: Map<number, string>,
        parentMap: Map<number, number>,
        rootUrl: string,
        assetBlobUrls?: Map<string, string>
    ): BundleParsedNode {
        // Get base node from parent (this resolves assetUri to a blob URL)
        const node = super.convertNode(
            documentNode, nodeIndex, document, nodeNames, parentMap, rootUrl, assetBlobUrls
        ) as BundleParsedNode;

        // Track original asset path for src. Bundled relative paths get a
        // `./assets/` prefix; absolute URLs (http(s), blob:) pass through
        // verbatim so they round-trip back through re-export without being
        // mangled into e.g. `./https://...`.
        const uri = node.originalAssetUri;
        if (uri) {
            if (uri.startsWith('http://') || uri.startsWith('https://') || uri.startsWith('blob:')) {
                node.originalAssetPath = uri;
            } else if (uri.startsWith('./')) {
                node.originalAssetPath = uri;
            } else {
                node.originalAssetPath = `./${uri}`;
            }
        }
        // Bundled assets come out of the ZIP, never off the network — the
        // folder URL the base plugin computed is meaningless here.
        node.resolvedSrcUrl = undefined;

        return node;
    }

    /**
     * Override to return Trace-specific LoadedSceneResult.
     *
     * Unlike the base GltfSceneLoaderPlugin which loads GLBs inline,
     * this skips asset loading — Model/Image/Video behaviors will load
     * from ResourceManager using the `src` path. Asset blobs are passed
     * through on the result for TraceScene to store in ResourceManager.
     */
    protected override async loadNodesWithResult(
        structure: ParsedDocumentStructure,
        scene: Scene,
        onProgress?: (event: ISceneLoaderProgressEvent) => void
    ): Promise<LoadedSceneResult> {
        const bundleStructure = structure as BundleParsedStructure;

        // No-op asset loader — a bundle never loads GLBs inline.
        // Model behavior handles asset loading from ResourceManager.
        const emptyResult = {
            meshes: [] as never[],
            animationGroups: [] as never[],
            skeletons: [] as never[],
            transformNodes: [] as never[],
            geometries: [] as never[],
            lights: [] as never[],
            particleSystems: [] as never[],
            spriteManagers: [] as never[],
        };
        const noOpLoadAsset = async () => emptyResult;

        const documentResult = await loadNodesWithResultUtil<ParsedDocumentNode, LoadedSceneNode>({
            nodes: structure.nodes,
            scene,
            onProgress,
            loadAsset: noOpLoadAsset,
            createLoadedNode: (transform, name) => ({
                transform,
                name,
            }),
            extractMetadata: (parsed, loaded) => {
                // Reuse base behavior/active/src forwarding from extras.trace.
                this.extractNodeMetadata(parsed, loaded);
                // Override src with the original bundled asset path (the base
                // sets it from `originalAssetUri`, the absolute URI; a bundle
                // wants the `./assets/foo.glb` form for ResourceManager).
                const bundleNode = parsed as BundleParsedNode;
                if (bundleNode.originalAssetPath) {
                    loaded.src = bundleNode.originalAssetPath;
                }
            },
        });

        return this.applyDocumentData(
            { ...documentResult, assetBlobs: bundleStructure.assetBlobs },
            bundleStructure,
        );
    }
}

/**
 * @deprecated Renamed to {@link BundleSceneLoaderPlugin} — the format is
 *   `.glz` now (spec §4). Kept for one release so consumers keep compiling.
 */
export const TraceXSceneLoaderPlugin = BundleSceneLoaderPlugin;

/** @deprecated Renamed to {@link BundleParsedNode}. */
export type TraceXParsedNode = BundleParsedNode;
